<template>
  <section class="events-page h-full w-full flex flex-col">
    <!-- Hero Section -->
    <div :style="backgroundStyle" class="events-hero relative w-full overflow-hidden">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div class="max-w-3xl">
            <div class="hero-badge">
              <span>Upcoming Events</span>
            </div>
            <h1 class="hero-title">Events & Calendars</h1>
            <p class="hero-subtitle">Discover workshops, seminars, networking opportunities, and stay updated with
              academic calendars</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="events-content bg-gradient-to-b from-gray-50 to-white pt-4 pb-12 sm:pt-6 sm:pb-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Breadcrumb -->
        <div class="mb-4">
          <bread-crumb :items="breadCrumbItems"></bread-crumb>
        </div>

        <!-- Section Header -->
        <div class="section-header mb-6">
          <div class="flex items-center gap-4">
            <button @click.stop="$router.go(-1)" class="back-button-icon">
              <icon-back class="icon"></icon-back>
            </button>
            <div>
              <h2 class="section-title">{{ getBannerText }}</h2>
              <p v-if="activeTab === 'events' && events.paginatorInfo && events.paginatorInfo.total"
                class="section-count">
                {{ formatNumber(events.paginatorInfo.total) }} {{ events.paginatorInfo.total === 1 ? 'event' : 'events'
                }} found
              </p>
            </div>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div class="tab-navigation mb-8">
          <div class="tab-list">
            <button @click="activeTab = 'events'" :class="activeTab === 'events' ? 'tab-active' : 'tab-inactive'"
              class="tab-button">
              Events
            </button>
            <button @click="activeTab = 'academic-calendar'"
              :class="activeTab === 'academic-calendar' ? 'tab-active' : 'tab-inactive'" class="tab-button">
              Academic Calendar
            </button>
            <button @click="activeTab = 'activities-calendar'"
              :class="activeTab === 'activities-calendar' ? 'tab-active' : 'tab-inactive'" class="tab-button">
              Activities Calendar
            </button>
            <button @click="activeTab = 'news-calendar'"
              :class="activeTab === 'news-calendar' ? 'tab-active' : 'tab-inactive'" class="tab-button">
              News Calendar
            </button>
          </div>
        </div>
        <transition name="fade" mode="out-in">
          <!-- Events Tab Content -->
          <div key="events" v-if="activeTab === 'events'" class="flex flex-col">
            <!-- Events View Toggle -->
            <div class="view-toggle mb-6">
              <button @click.stop="eventsViewMode = 'list'"
                :class="eventsViewMode === 'list' ? 'toggle-active' : 'toggle-inactive'" class="toggle-button">
                <font-awesome-icon icon="fa-solid fa-th-large" class="toggle-icon"></font-awesome-icon>
                <span>List View</span>
              </button>
              <button @click.stop="eventsViewMode = 'calendar'"
                :class="eventsViewMode === 'calendar' ? 'toggle-active' : 'toggle-inactive'" class="toggle-button">
                <icon-calendar-days class="toggle-icon"></icon-calendar-days>
                <span>Calendar View</span>
              </button>
            </div>

            <!-- Events List View -->
            <div v-if="eventsViewMode === 'list'" class="flex flex-col">
              <!-- Filters -->
              <div class="filters-section mb-8">
                <div class="filters-row">
                  <div class="filter-buttons">
                    <button @click.stop="eventType = 'all'"
                      :class="eventType === 'all' ? 'filter-active' : 'filter-inactive'" class="filter-button">
                      All
                    </button>
                    <button @click.stop="eventType = 'upcoming'"
                      :class="eventType === 'upcoming' ? 'filter-active' : 'filter-inactive'" class="filter-button">
                      Upcoming Events
                    </button>
                    <button @click.stop="eventType = 'past'"
                      :class="eventType === 'past' ? 'filter-active' : 'filter-inactive'" class="filter-button">
                      Past Events
                    </button>
                  </div>
                  <div class="entity-filter">
                    <label class="entity-label">Entity:</label>
                    <select v-model="filterByEntity" class="entity-select">
                      <option value="">All</option>
                      <option :value="item.id" v-for="(item, index) in entitiesList" :key="index">
                        {{ getEntityName(item.entity) }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="$apollo.queries.events.loading && page === 1" class="events-grid">
                <events-card-shimmer v-for="(story, index) in first" :key="index"></events-card-shimmer>
              </div>

              <!-- Events Grid -->
              <div v-else-if="hasEvents" class="events-grid">
                <events-card v-for="(event, index) in getEvents" :key="index" :item="event"></events-card>
              </div>

              <!-- Empty State -->
              <div v-else class="empty-state">
                <div class="empty-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                    </path>
                  </svg>
                </div>
                <h3 class="empty-title">No Events Found</h3>
                <p class="empty-text">There are no events available matching your filters. Please try different filters
                  or check back later.</p>
              </div>

              <!-- Load More Button -->
              <div v-if="showViewMoreButton && hasEvents" class="load-more-wrapper">
                <button @click="loadMore" class="load-more-btn group" :disabled="$apollo.queries.events.loading">
                  <span v-if="!$apollo.queries.events.loading">Load More Events</span>
                  <span v-else class="loading-text">Loading...</span>
                  <svg v-if="!$apollo.queries.events.loading" class="load-more-icon" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                  <svg v-else class="load-more-icon animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Events Calendar View -->
            <div v-else class="flex flex-col">
              <event-calendar class="calendar-view"></event-calendar>
            </div>
          </div>

          <!-- Academic Calendar Tab Content -->
          <div key="academic-calendar" v-else-if="activeTab === 'academic-calendar'" class="flex flex-col">
            <academic-calendar class="calendar-view"></academic-calendar>
          </div>

          <!-- Activities Calendar Tab Content -->
          <div key="activities-calendar" v-else-if="activeTab === 'activities-calendar'" class="flex flex-col">
            <activities-calendar class="calendar-view"></activities-calendar>
          </div>

          <!-- News Calendar Tab Content -->
          <div key="news-calendar" v-else class="flex flex-col">
            <news-calendar class="calendar-view"></news-calendar>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
import gql from 'graphql-tag';
import EventsCard from "../../components/events/EventsCard.vue";
import EventsCardShimmer from "../../components/events/EventsCardShimmer.vue";
import IconBack from "../../components/icons/IconBack.vue";
import IconCalendarDays from "../../components/icons/IconCalendarDays.vue";
import EventCalendar from "./EventCalendar.vue";
import AcademicCalendar from "./AcademicCalendar.vue";
import ActivitiesCalendar from "./ActivitiesCalendar.vue";
import NewsCalendar from "./NewsCalendar.vue";
import BreadCrumb from "../../components/shared/BreadCrumb.vue";
import backgroundImage from '../../assets/images/events/event_banner.png';

const itemPerPage = 12;

export default {
  name: "events",
  components: {
    EventsCard,
    EventsCardShimmer,
    IconBack,
    IconCalendarDays,
    EventCalendar,
    AcademicCalendar,
    ActivitiesCalendar,
    NewsCalendar,
    BreadCrumb,
  },
  data() {
    return {
      events: [],
      first: itemPerPage,
      page: 1,
      activeTab: "events",
      eventsViewMode: "list",
      breadCrumbItems: [
        { label: 'Home', path: '/' },
        { label: 'Events' },
      ],
      eventType: "all",
      entitiesList: [],
      filterByEntity: "",
    };
  },
  watch: {
    eventType() {
      this.page = 1;
    },
    filterByEntity() {
      this.page = 1;
    },
  },
  computed: {
    backgroundStyle() {
      return {
        'background-image': `url(${backgroundImage})`
      }
    },
    getBannerText() {
      if (this.activeTab == 'events') {
        return "Events";
      } else if (this.activeTab == 'academic-calendar') {
        return "Academic Calendar";
      } else if (this.activeTab == 'activities-calendar') {
        return "Activities Calendar";
      } else {
        return "News Calendar";
      }
    },
    getEvents() {
      if (!this.events || !this.events.data) {
        return [];
      }
      return this.events.data;
    },
    showViewMoreButton() {
      if (!this.events || !this.events.paginatorInfo) {
        return false;
      }
      return this.events.paginatorInfo.hasMorePages;
    },
    hasEvents() {
      if (this.getEvents && this.getEvents.length > 0) {
        return true;
      } else {
        return false;
      }
    },
  },
  apollo: {
    events() {
      return {
        query: gql`query GetUpcomingEvents($first: Int!, $page: Int!, $event_type: String, $entity_id: Int) {
                  events(first: $first, page: $page, event_type: $event_type, entity_id: $entity_id) {
                      data {
                        slug
                        description
                        id
                        thumbnail
                        title
                        start_date
                        end_date
                        location
                        display_time
                        display_day
                        entity {
                            logo
                            entity
                        }
                      }
                      paginatorInfo {
                          count
                          currentPage
                          firstItem
                          hasMorePages
                          lastItem
                          lastPage
                          perPage
                          total
                      }
                  }
              }`,
        variables() {
          return {
            first: itemPerPage,
            page: 1,
            event_type: this.eventType,
            entity_id: parseInt(this.filterByEntity),
          }
        },
        fetchPolicy: 'cache-and-network'
      }
    },
    entitiesList() {
      return {
        query: gql`query GetAllEntities {
            entitiesList {
              id
              entity
            }
          }`,
      }
    },
  },
  methods: {
    formatNumber(num) {
      if (!num) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    loadMore() {
      this.page += 1;
      this.$apollo.queries.events.fetchMore({
        variables: {
          first: this.first,
          page: this.page,
        },

        updateQuery: (previousResult, { fetchMoreResult }) => {

          const newEvents = fetchMoreResult.events.data;
          const newPaginatorInfo = fetchMoreResult.events.paginatorInfo;

          return {
            events: {
              __typename: previousResult.events.__typename,
              data: [...previousResult.events.data, ...newEvents],
              paginatorInfo: newPaginatorInfo,
            }
          }

        }
      })
    },
    getEntityName(entity) {
      if (entity == "ATHS") {
        return "ATS";
      } else {
        return entity;
      }
    },
  },

}
</script>
<style lang="scss" scoped>
.events-hero {
  height: 400px;
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  @media (min-width: 640px) {
    height: 450px;
  }

  @media (min-width: 1024px) {
    height: 500px;
  }
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(57, 72, 103, 0.85) 0%, rgba(73, 157, 212, 0.75) 100%);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  height: 100%;
  color: white;
}

.hero-badge {
  display: inline-block;
  margin-bottom: 1rem;
}

.hero-badge span {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 1024px) {
    font-size: 3.5rem;
  }
}

.hero-subtitle {
  font-size: 1.125rem;
  line-height: 1.7;
  opacity: 0.95;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
}

.section-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.back-button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(57, 72, 103, 0.3);
  }

  .icon {
    width: 1.25rem;
    height: 1.25rem;
  }
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
  line-height: 1.3;

  @media (min-width: 640px) {
    font-size: 1.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
}

.section-count {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;

  @media (min-width: 640px) {
    font-size: 1rem;
  }
}

.tab-navigation {
  border-bottom: 2px solid #e2e8f0;
}

.tab-list {
  display: flex;
  gap: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tab-button {
  padding: 0.875rem 1.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
  white-space: nowrap;
  color: #64748b;

  &:hover {
    color: #394867;
    background-color: #f8fafc;
  }

  @media (min-width: 640px) {
    padding: 1rem 2rem;
    font-size: 1rem;
  }
}

.tab-active {
  color: #394867;
  border-bottom-color: #499DD4;
  background-color: transparent;
}

.tab-inactive {
  color: #64748b;
}

.view-toggle {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.toggle-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #499DD4;
    color: #499DD4;
  }

  .toggle-icon {
    width: 1rem;
    height: 1rem;
  }
}

.toggle-active {
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-color: transparent;

  &:hover {
    background: linear-gradient(135deg, #499DD4 0%, #394867 100%);
    color: white;
  }
}

.toggle-inactive {
  color: #64748b;
}

.filters-section {
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.filters-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.filter-button {
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 2px solid #394867;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  @media (min-width: 640px) {
    font-size: 0.9375rem;
    padding: 0.75rem 1.5rem;
  }
}

.filter-active {
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-color: transparent;

  &:hover {
    background: linear-gradient(135deg, #499DD4 0%, #394867 100%);
  }
}

.filter-inactive {
  background: white;
  color: #394867;

  &:hover {
    background: #f8fafc;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
}

.entity-filter {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.entity-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
}

.entity-select {
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid #e2e8f0;
  background: white;
  font-size: 0.875rem;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;

  &:hover {
    border-color: #499DD4;
  }

  &:focus {
    outline: none;
    border-color: #499DD4;
    box-shadow: 0 0 0 3px rgba(73, 157, 212, 0.1);
  }
}

.events-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 5rem;
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  border-radius: 50%;
  margin-bottom: 1.5rem;
  color: #9ca3af;

  svg {
    width: 2.5rem;
    height: 2.5rem;
  }
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;

  @media (min-width: 640px) {
    font-size: 1.875rem;
  }
}

.empty-text {
  font-size: 1rem;
  color: #64748b;
  max-width: 32rem;
  margin: 0 auto;

  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
}

.load-more-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.load-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #394867 0%, #499DD4 100%);
  color: white;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px -2px rgba(0, 0, 0, 0.15);
    background: linear-gradient(135deg, #499DD4 0%, #394867 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .load-more-icon {
    width: 1.25rem;
    height: 1.25rem;
    transition: transform 0.3s ease;
  }

  &:hover:not(:disabled) .load-more-icon {
    transform: translateY(4px);
  }
}

.loading-text {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.calendar-view {
  margin-top: 2rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>